import { useState, useEffect } from "react";
import axios from "axios";
import { useAuthUser, useSignOut } from "react-auth-kit";
import {
  KeyboardDoubleArrowLeft,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Menu,
  Logout,
  ManageAccounts,
  People,
  Settings,
  TimeToLeave,
  Topic,
  Assignment,
  Bookmark,
  CoPresent,
  MobiledataOff,
  StorefrontOutlined,
  SwapVert,
  Token,
} from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { TreeItem, TreeView } from "@mui/lab";
import { Box, Tooltip, Typography } from "@mui/material";
import localize from "../common/localize";
import ApplicationLogoHorizontal from "./common/ApplicationLogoHorizontal";
import {
  RemoveFiltersFromLocalStorage,
  defaultFilteringForEvent,
  defaultFilteringForNewness,
} from "../common/EntitiesManagement";
import useGlobalContext from "../hooks/useGlobalContext";
import ModalBackground from "./common/ModalBackground";
import SignOut from "./common/SignOut";

const DropdownMenuItem = ({
  title,
  link,
  setshowSideBar,
  icon,
  marginOnTop = 3,
  setEventFilters,
  setNewnessFilters,
}) => (
  <Link
    style={{ marginTop: marginOnTop, borderRadius: "40px" }}
    className="main_menu w-full py-2 px-4 flex items-center gap-3 whitespace-no-wrap text-m"
    to={link}
    onClick={() => {
      setEventFilters(defaultFilteringForEvent(true));
      setNewnessFilters(defaultFilteringForNewness(true));
      setshowSideBar(false);
    }}
  >
    {icon && icon}
    {title}
  </Link>
);

export default function Sidebar() {
  const { setFilterForEvents, setFilterForNewness, AUTH_ENDPOINT, config } =
    useGlobalContext();
  const [userRoles, setUserRoles] = useState(undefined);
  const auth = useAuthUser();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const userData = auth();
  const [showSidebar, setShowSidebar] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const backColor = "#10366E";
  const fontColor = "white";

  const menuStyle = {
    fontSize: "0.8em",
    backgroundColor: backColor,
    color: fontColor,
    padding: "10px 5px 10px 5px",
  };

  useEffect(() => {
    if (userData) {
      setUserRoles(userData.UserRoles);
    }
  }, [userData]);

  const getModuleRestricted = (moduleNames) => {
    if (userRoles && moduleNames?.length > 0) {
      const restrictedModulesFound = moduleNames.map((module) =>
        userRoles[0].Description?.match(
          `{Form: ${module}, Restriction: ACCESS}`
        )
      );

      const array = restrictedModulesFound.filter((x) => x != null);
      return array?.length === moduleNames?.length;
    }

    return false;
  };

  const requestLogOut = async () => {
    await axios.put(
      `${AUTH_ENDPOINT}/logout/${userData?.SessionId}`,
      {},
      config
    );
  };

  return (
    <>
      {showSignOutModal && (
        <ModalBackground
          okButtonText={localize.sign_out}
          title={localize.app_name}
          showModal={showSignOutModal}
          setShowModal={setShowSignOutModal}
          onClickToCancel={() => {
            setShowSignOutModal(false);
          }}
          onClickToSubmit={() => {
            setShowSignOutModal(false);
            navigate("/");
            requestLogOut();
            signOut();
          }}
          Component={<SignOut />}
        />
      )}
      {!showSidebar && (
        <Tooltip title={localize.tooltip_open_menu}>
          <button
            onClick={() => setShowSidebar(!showSidebar)}
            className="flex items-center cursor-pointer"
          >
            <Menu className="text-3xl text-black menu_foreground_color" />
          </button>
        </Tooltip>
      )}

      <div
        className={`menu_background_color top-0 left-0 py-2 px-4 shadow-xl fixed h-full z-40 ease-in duration-500 flex flex-col justify-between ${
          showSidebar ? "translate-x-0 " : "hidden"
        }`}
        style={{
          overflow: "auto",
          boxShadow: "2px 2px 2px 1px rgba(0, 0, 0, 0.2)",
        }}
      >
        <div className="flex flex-col" style={{ width: "280px" }}>
          <div style={{ display: "flex", gap: "20px", alignContent: "center" }}>
            <Tooltip title={localize.tooltip_close_menu}>
              <button onClick={() => setShowSidebar(!showSidebar)}>
                <KeyboardDoubleArrowLeft className="text-4xl text-black menu_foreground_color" />
              </button>
            </Tooltip>

            <Tooltip title={localize.home}>
              <Link
                className="font-bold text-black menu_foreground_color"
                to="/"
                onClick={() => {
                  RemoveFiltersFromLocalStorage();
                }}
              >
                <ApplicationLogoHorizontal height={20} width={110} />
              </Link>
            </Tooltip>
          </div>

          <div
            style={{
              marginTop: 20,
            }}
          >
            <TreeView>
              {!getModuleRestricted([
                "OPERATIVE_AREA",
                "EVENT_TYPE",
                "FORM_FIELD",
                "DYNAMIC_FORM",
                "EXECUTION_STATUS",
                "WORKFLOW",
                "ADDITIONAL_NEWNESS",
                "ADDITIONAL_COST_TYPE",
                "PAYMENT_TYPE",
                "EVIDENCE_TEMPLATE",
                "REPORT_TYPE",
              ]) && (
                <TreeItem
                  sx={menuStyle}
                  expandIcon={
                    <KeyboardArrowDown style={{ color: fontColor }} />
                  }
                  collapseIcon={
                    <KeyboardArrowUp style={{ color: fontColor }} />
                  }
                  label={
                    <Box className="flex gap-2 items-center">
                      <Settings sx={{ width: "18px", height: "18px" }} />{" "}
                      {localize.main_menu_process_management}
                    </Box>
                  }
                  nodeId={localize.main_menu_process_management}
                >
                  {!getModuleRestricted(["OPERATIVE_AREA"]) && (
                    <DropdownMenuItem
                      title={localize.operational_areas}
                      link="/operational-area"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {!getModuleRestricted(["EVENT_TYPE"]) && (
                    <DropdownMenuItem
                      title={localize.event_type_title}
                      link="/eventtype"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {!getModuleRestricted(["FORM_FIELD"]) && (
                    <DropdownMenuItem
                      title={localize.form_fields}
                      link="/formfields"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {!getModuleRestricted(["DYNAMIC_FORM"]) && (
                    <DropdownMenuItem
                      title={localize.dynamic_forms}
                      link="/dynamicforms"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {!getModuleRestricted(["EXECUTION_STATUS"]) && (
                    <DropdownMenuItem
                      title={localize.execution_statuses}
                      link="/status"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {!getModuleRestricted(["WORKFLOW"]) && (
                    <DropdownMenuItem
                      title={localize.workflows}
                      link="/workflow"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {!getModuleRestricted(["ADDITIONAL_NEWNESS"]) && (
                    <DropdownMenuItem
                      title={localize.additionalnewnesssetup}
                      link="/additionalnewness"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {!getModuleRestricted(["ADDITIONAL_COST_TYPE"]) && (
                    <DropdownMenuItem
                      title={localize.additional_cost_type_title}
                      link="/additionalCostType"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["PAYMENT_TYPE"]) && (
                    <DropdownMenuItem
                      title={localize.payment_type_title}
                      link="/paymentType"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["EVIDENCE_TEMPLATE"]) && (
                    <DropdownMenuItem
                      title={localize.evidence_template}
                      link="/evidencetemplates"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["REPORT_TYPE"]) && (
                    <DropdownMenuItem
                      title={localize.report_types}
                      link="/reportType"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                </TreeItem>
              )}

              {!getModuleRestricted([
                "EVENT",
                "DAILY_ROUTE",
                "PRE_ORDER",
                "REPORTED_NEWNESS",
                "REGISTER_PAYMENTS",
                "RELATED_BILLS",
              ]) && (
                <TreeItem
                  sx={menuStyle}
                  expandIcon={
                    <KeyboardArrowDown style={{ color: fontColor }} />
                  }
                  collapseIcon={
                    <KeyboardArrowUp style={{ color: fontColor }} />
                  }
                  label={
                    <Box className="flex gap-2 items-center">
                      <Assignment sx={{ width: "18px", height: "18px" }} />
                      {localize.main_menu_event_management}
                    </Box>
                  }
                  nodeId={localize.main_menu_event_management}
                >
                  {!getModuleRestricted(["EVENT"]) && (
                    <DropdownMenuItem
                      title={localize.events}
                      link="/events"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {!getModuleRestricted(["DAILY_ROUTE"]) && (
                    <DropdownMenuItem
                      title={localize.daily_route_title}
                      link="/dailyRoute"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {!getModuleRestricted(["PRE_ORDER"]) && (
                    <DropdownMenuItem
                      title={localize.pre_orders_title}
                      link="/preorder"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {!getModuleRestricted(["REPORTED_NEWNESS"]) && (
                    <DropdownMenuItem
                      title={localize.reported_newness}
                      link="/newness"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {!getModuleRestricted(["REGISTER_PAYMENTS"]) && (
                    <DropdownMenuItem
                      title={localize.register_payment}
                      link="/registerpayments"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {!getModuleRestricted(["RELATED_BILLS"]) && (
                    <DropdownMenuItem
                      title={localize.related_bills}
                      link="/bills"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                </TreeItem>
              )}

              {!getModuleRestricted(["REQUEST_TYPE", "GENERAL_REQUESTS"]) && (
                <TreeItem
                  sx={menuStyle}
                  expandIcon={
                    <KeyboardArrowDown style={{ color: fontColor }} />
                  }
                  collapseIcon={
                    <KeyboardArrowUp style={{ color: fontColor }} />
                  }
                  label={
                    <Box className="flex gap-2 items-center">
                      <Bookmark sx={{ width: "18px", height: "18px" }} />
                      {localize.main_menu_general_request}
                    </Box>
                  }
                  nodeId={localize.main_menu_general_request}
                >
                  {!getModuleRestricted(["REQUEST_TYPE"]) && (
                    <DropdownMenuItem
                      title={localize.request_types}
                      link="/requestType"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {!getModuleRestricted(["GENERAL_REQUESTS"]) && (
                    <DropdownMenuItem
                      title={localize.general_requests}
                      link="/generalRequests"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {/* {!getModuleRestricted(["QUOTATION_SETUP"]) && (
                    <DropdownMenuItem
                      title={localize.setup}
                      link="/quotationSetup"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )} */}
                </TreeItem>
              )}

              {/*!getModuleRestricted([
                "QUOTATION_REQUEST",
                "QUOTATION_SETUP",
              ]) && (
                <TreeItem
                  sx={menuStyle}
                  expandIcon={
                    <KeyboardArrowDown style={{ color: fontColor }} />
                  }
                  collapseIcon={
                    <KeyboardArrowUp style={{ color: fontColor }} />
                  }
                  label={localize.main_menu_quotation_management}
                  nodeId={localize.main_menu_quotation_management}
                >
                  {!getModuleRestricted(["QUOTATION_REQUEST"]) && (
                    <DropdownMenuItem
                      title={localize.requests}
                      link="/quotationRequest"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {!getModuleRestricted(["QUOTATION_SETUP"]) && (
                    <DropdownMenuItem
                      title={localize.setup}
                      link="/quotationSetup"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                </TreeItem>
              ) */}

              {!getModuleRestricted(["CONTRACTOR"]) && (
                <TreeItem
                  sx={menuStyle}
                  expandIcon={
                    <KeyboardArrowDown style={{ color: fontColor }} />
                  }
                  collapseIcon={
                    <KeyboardArrowUp style={{ color: fontColor }} />
                  }
                  label={
                    <Box className="flex gap-2 items-center">
                      <StorefrontOutlined
                        sx={{ width: "18px", height: "18px" }}
                      />
                      {localize.main_menu_contractor_management}
                    </Box>
                  }
                  nodeId={localize.main_menu_contractor_management}
                >
                  {/* Contractor management */}
                  {!getModuleRestricted(["CONTRACTOR"]) && (
                    <DropdownMenuItem
                      title={localize.contractors}
                      link="/contractor"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                </TreeItem>
              )}

              {!getModuleRestricted([
                "POSITION",
                "PERSONAL",
                /*,'WORK_TEAM'*/
              ]) && (
                <TreeItem
                  sx={menuStyle}
                  expandIcon={
                    <KeyboardArrowDown style={{ color: fontColor }} />
                  }
                  collapseIcon={
                    <KeyboardArrowUp style={{ color: fontColor }} />
                  }
                  label={
                    <Box className="flex gap-2 items-center">
                      <People sx={{ width: "18px", height: "18px" }} />
                      {localize.main_menu_personal_management}
                    </Box>
                  }
                  nodeId={localize.main_menu_personal_management}
                >
                  {!getModuleRestricted(["POSITION"]) && (
                    <DropdownMenuItem
                      title={localize.positions}
                      link="/position"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {!getModuleRestricted(["PERSONAL"]) && (
                    <DropdownMenuItem
                      title={localize.personal}
                      link="/personal"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {/* !getModuleRestricted(['WORK_TEAM']) && (
                  <DropdownMenuItem
                    title={localize.work_team}
                    link="/teams"
                    setshowSideBar={setShowSidebar}
                    setEventFilters={setFilterForEvents}
                    setNewnessFilters={setFilterForNewness}
                  />
                ) */}
                </TreeItem>
              )}

              {!getModuleRestricted([
                "MEASUREMENT_UNIT",
                "SUPPLY",
                "PROVIDER",
              ]) && (
                <TreeItem
                  sx={menuStyle}
                  expandIcon={
                    <KeyboardArrowDown style={{ color: fontColor }} />
                  }
                  collapseIcon={
                    <KeyboardArrowUp style={{ color: fontColor }} />
                  }
                  label={
                    <Box className="flex gap-2 items-center">
                      <Token sx={{ width: "18px", height: "18px" }} />
                      {localize.main_menu_supply_management}
                    </Box>
                  }
                  nodeId={localize.main_menu_supply_management}
                >
                  {!getModuleRestricted(["MEASUREMENT_UNIT"]) && (
                    <DropdownMenuItem
                      title={localize.measurement_units}
                      link="/measurementunit"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {!getModuleRestricted(["SUPPLY"]) && (
                    <DropdownMenuItem
                      title={localize.supplies}
                      link="/supply"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {!getModuleRestricted(["PROVIDER"]) && (
                    <DropdownMenuItem
                      title={localize.providers}
                      link="/provider"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                </TreeItem>
              )}

              {!getModuleRestricted([
                "INVENTORY_MOVEMENT_TYPE",
                "INVENTORY_MOVEMENT",
                //"WAREHOUSE",
              ]) && (
                <TreeItem
                  sx={menuStyle}
                  expandIcon={
                    <KeyboardArrowDown style={{ color: fontColor }} />
                  }
                  collapseIcon={
                    <KeyboardArrowUp style={{ color: fontColor }} />
                  }
                  label={
                    <Box className="flex gap-2 items-center">
                      <SwapVert sx={{ width: "18px", height: "18px" }} />
                      {localize.main_menu_inventory_management}
                    </Box>
                  }
                  nodeId={localize.main_menu_inventory_management}
                >
                  {!getModuleRestricted(["INVENTORY_MOVEMENT_TYPE"]) && (
                    <DropdownMenuItem
                      title={localize.inventory_movement_type}
                      link="/inventoryMovementType"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {!getModuleRestricted(["INVENTORY_MOVEMENT"]) && (
                    <DropdownMenuItem
                      title={localize.inventory_movement_title}
                      link="/inventoryMovement"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {/*!getModuleRestricted(["WAREHOUSE"]) && (
                    <DropdownMenuItem
                      title={localize.warehouses}
                      link="/warehouse"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )*/}
                </TreeItem>
              )}

              {!getModuleRestricted(["CLIENT"]) && (
                <TreeItem
                  sx={menuStyle}
                  expandIcon={
                    <KeyboardArrowDown style={{ color: fontColor }} />
                  }
                  collapseIcon={
                    <KeyboardArrowUp style={{ color: fontColor }} />
                  }
                  label={
                    <Box className="flex items-center gap-2">
                      <CoPresent sx={{ width: "18px", height: "18px" }} />
                      {localize.main_menu_client_management}
                    </Box>
                  }
                  nodeId={localize.main_menu_client_management}
                >
                  {!getModuleRestricted(["CLIENT"]) && (
                    <DropdownMenuItem
                      title={localize.clients}
                      link="/client"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                </TreeItem>
              )}

              {!getModuleRestricted(["PROCEEDING", "RESOLUTION"]) && (
                <TreeItem
                  sx={menuStyle}
                  expandIcon={
                    <KeyboardArrowDown style={{ color: fontColor }} />
                  }
                  collapseIcon={
                    <KeyboardArrowUp style={{ color: fontColor }} />
                  }
                  label={
                    <Box className="flex gap-2 items-center">
                      <Topic sx={{ width: "18px", height: "18px" }} />
                      {localize.main_menu_proceedings}
                    </Box>
                  }
                  nodeId={localize.main_menu_proceedings}
                >
                  {!getModuleRestricted(["PROCEEDING"]) && (
                    <DropdownMenuItem
                      title={localize.proceedings}
                      link="/proceedings"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {!getModuleRestricted(["RESOLUTION"]) && (
                    <DropdownMenuItem
                      title={localize.resolutions}
                      link="/resolutions"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                </TreeItem>
              )}

              {!getModuleRestricted(["VEHICLE_TYPE", "VEHICLE"]) && (
                <TreeItem
                  sx={menuStyle}
                  expandIcon={
                    <KeyboardArrowDown style={{ color: fontColor }} />
                  }
                  collapseIcon={
                    <KeyboardArrowUp style={{ color: fontColor }} />
                  }
                  label={
                    <Box className="flex gap-2 items-center">
                      <TimeToLeave sx={{ width: "18px", height: "18px" }} />
                      {localize.main_menu_vehicle_management}
                    </Box>
                  }
                  nodeId={localize.main_menu_vehicle_management}
                >
                  {!getModuleRestricted(["VEHICLE_TYPE"]) && (
                    <DropdownMenuItem
                      title={localize.vehicle_types}
                      link="/vehicletypes"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                  {!getModuleRestricted(["VEHICLE"]) && (
                    <DropdownMenuItem
                      title={localize.vehicles}
                      link="/vehicles"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                </TreeItem>
              )}

              {!getModuleRestricted(["PROFILE", "USER"]) && (
                <TreeItem
                  sx={menuStyle}
                  expandIcon={
                    <KeyboardArrowDown style={{ color: fontColor }} />
                  }
                  collapseIcon={
                    <KeyboardArrowUp style={{ color: fontColor }} />
                  }
                  label={
                    <Box className="flex gap-2 items-center">
                      <ManageAccounts sx={{ width: "18px", height: "18px" }} />
                      {localize.main_menu_user_management}
                    </Box>
                  }
                  nodeId={localize.main_menu_user_management}
                >
                  {!getModuleRestricted(["PROFILE"]) && (
                    <DropdownMenuItem
                      title={localize.profiles}
                      link="/profiles"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["USER"]) && (
                    <DropdownMenuItem
                      title={localize.users}
                      link="/users"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                </TreeItem>
              )}
            </TreeView>
          </div>
        </div>
        <div className="w-full flex justify-between items-center">
          <div className="flex" style={{ gap: "10px", cursor: "pointer" }}>
            <Logout
              onClick={() => {
                setShowSignOutModal(true);
              }}
            />{" "}
            <Typography
              onClick={() => {
                setShowSignOutModal(true);
              }}
            >
              {localize.tooltip_logout}
            </Typography>
          </div>
          <div>
            <label style={{ fontSize: "10px" }}>{localize.version}</label>
          </div>
        </div>
      </div>
    </>
  );
}
